import { studentShortLabel } from "@/lib/utils-app";
import StudentPicker from "./StudentPicker";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  DndContext, PointerSensor, useSensor, useSensors, closestCorners, DragOverlay,
} from "@dnd-kit/core";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import {
  LayoutGrid, Table as TableIcon, Filter, Plus, Search, X, MoveRight,
  User, Clock, DollarSign, Percent, ChevronRight, Trash2, Edit3, History,
} from "lucide-react";

/**
 * V1.30 · ANA · Pipeline comercial.
 *
 * Vista Kanban (drag & drop desktop) + Vista Tabla (dropdown de stage,
 * mejor experiencia móvil). El usuario decide cuál usar con un toggle.
 *
 * Cada mover de stage puede devolver una `lifecycle_suggestion` — se
 * muestra como toast interactivo (SIN cambio automático del alumno).
 */
export default function AnaPipelinePanel() {
  const [view, setView] = useState("kanban");   // "kanban" | "table"
  const [catalog, setCatalog] = useState({ stages: [], products: [], lifecycle_suggestions: {} });
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [productFilter, setProductFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [editorOpp, setEditorOpp] = useState(null);
  const [moveModal, setMoveModal] = useState(null);   // { opp, to }
  const [historyOpp, setHistoryOpp] = useState(null);
  const [activeDragId, setActiveDragId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, o, u] = await Promise.all([
        api.get("/ana/pipeline/catalog"),
        api.get("/ana/opportunities"),
        api.get("/ana/users"),
      ]);
      setCatalog(c.data || {});
      setOpportunities(o.data.opportunities || []);
      setUsers(u.data.users || []);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => opportunities.filter((o) => {
    if (ownerFilter !== "all" && o.owner_email !== ownerFilter) return false;
    if (productFilter !== "all" && o.product_slug !== productFilter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      return (o.title || "").toLowerCase().includes(q)
          || (o.notes || "").toLowerCase().includes(q)
          || (o.student?.name || "").toLowerCase().includes(q);
    }
    return true;
  }), [opportunities, ownerFilter, productFilter, search]);

  const oppsByStage = useMemo(() => {
    const map = {};
    (catalog.stages || []).forEach((s) => { map[s.key] = []; });
    filtered.forEach((o) => {
      if (!map[o.stage]) map[o.stage] = [];
      map[o.stage].push(o);
    });
    return map;
  }, [filtered, catalog.stages]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const performMove = async (opp, toStage, extra = {}) => {
    if (opp.stage === toStage) return;
    // Move a "no_interesado" o "pagada" ⇒ requerimos modal para note/importe.
    if (toStage === "no_interesado" || toStage === "pagada") {
      setMoveModal({ opp, to: toStage });
      return;
    }
    try {
      const { data } = await api.post(`/ana/opportunities/${opp.id}/move-stage`, { to: toStage, ...extra });
      toast.success(`Movida a "${data.opportunity.stage}"`);
      // Sugerencia lifecycle (nunca automática)
      if (data.lifecycle_suggestion) {
        showLifecycleSuggestion(data.lifecycle_suggestion);
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const showLifecycleSuggestion = (sug) => {
    toast(sug.message, {
      description: `Alumno: ${sug.student_name}`,
      duration: 12000,
      action: {
        label: "Sí, aplicar",
        onClick: async () => {
          try {
            await api.patch(`/students/${sug.student_id}/lifecycle-stage`, {
              lifecycle_stage: sug.suggested_lifecycle_stage,
              note: `Actualizado desde pipeline ANA (V1.30)`,
            });
            toast.success("Estado del alumno actualizado");
            load();
          } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
          }
        },
      },
    });
  };

  // ---- Drag & Drop ----
  const handleDragEnd = (event) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over) return;
    const opp = opportunities.find((o) => o.id === active.id);
    if (!opp) return;
    const toStage = over.id;
    if (opp.stage === toStage) return;
    performMove(opp, toStage);
  };

  const activeOpp = useMemo(() => opportunities.find((o) => o.id === activeDragId), [opportunities, activeDragId]);

  return (
    <div className="space-y-3" data-testid="ana-pipeline-panel">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-lg border border-neutral-300 p-0.5" role="tablist">
          <button
            type="button"
            onClick={() => setView("kanban")}
            data-testid="ana-pipeline-view-kanban"
            className={"text-xs font-semibold px-2.5 py-1 rounded inline-flex items-center gap-1 " + (view === "kanban" ? "bg-orange-500 text-white" : "text-neutral-600 hover:text-neutral-900")}
          >
            <LayoutGrid className="h-3.5 w-3.5" /> Kanban
          </button>
          <button
            type="button"
            onClick={() => setView("table")}
            data-testid="ana-pipeline-view-table"
            className={"text-xs font-semibold px-2.5 py-1 rounded inline-flex items-center gap-1 " + (view === "table" ? "bg-orange-500 text-white" : "text-neutral-600 hover:text-neutral-900")}
          >
            <TableIcon className="h-3.5 w-3.5" /> Tabla
          </button>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar…"
            data-testid="ana-pipeline-search"
            className="rounded-full border border-neutral-300 bg-white pl-8 pr-3 h-8 text-xs focus:outline-none focus:ring-2 focus:ring-orange-500 w-56"
          />
        </div>
        <select value={productFilter} onChange={(e) => setProductFilter(e.target.value)} data-testid="ana-pipeline-filter-product" className="rounded-full border border-neutral-300 bg-white px-3 h-8 text-xs focus:outline-none focus:ring-2 focus:ring-orange-500">
          <option value="all">Todos los productos</option>
          {(catalog.products || []).map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
        </select>
        <select value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)} data-testid="ana-pipeline-filter-owner" className="rounded-full border border-neutral-300 bg-white px-3 h-8 text-xs focus:outline-none focus:ring-2 focus:ring-orange-500">
          <option value="all">Todos los responsables</option>
          {users.map((u) => <option key={u.email} value={u.email}>{u.name || u.email}</option>)}
        </select>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-neutral-500">{filtered.length} oportunidades</span>
          <button
            type="button"
            onClick={() => setCreatorOpen(true)}
            data-testid="ana-pipeline-create"
            className="btn-primary"
          >
            <Plus className="h-3.5 w-3.5" /> Nueva
          </button>
        </div>
      </div>

      {loading ? (
        <div className="card-surface p-8 text-center text-neutral-500">Cargando pipeline…</div>
      ) : view === "kanban" ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={(e) => setActiveDragId(e.active.id)}
          onDragEnd={handleDragEnd}
        >
          <div className="grid grid-flow-col auto-cols-[280px] gap-3 overflow-x-auto pb-4" data-testid="ana-pipeline-kanban">
            {(catalog.stages || []).map((s) => (
              <StageColumn
                key={s.key}
                stage={s}
                opps={oppsByStage[s.key] || []}
                onOpen={setEditorOpp}
                onMove={performMove}
                stages={catalog.stages}
                onHistory={setHistoryOpp}
              />
            ))}
          </div>
          <DragOverlay>
            {activeOpp && (
              <OpportunityCard opp={activeOpp} dragging />
            )}
          </DragOverlay>
        </DndContext>
      ) : (
        <PipelineTable
          opportunities={filtered}
          stages={catalog.stages}
          onOpen={setEditorOpp}
          onMove={performMove}
          onHistory={setHistoryOpp}
        />
      )}

      {creatorOpen && (
        <OpportunityCreator
          catalog={catalog}
          users={users}
          onClose={() => setCreatorOpen(false)}
          onCreated={() => { setCreatorOpen(false); load(); }}
        />
      )}
      {editorOpp && (
        <OpportunityEditor
          opp={editorOpp}
          catalog={catalog}
          users={users}
          onClose={() => setEditorOpp(null)}
          onSaved={() => { setEditorOpp(null); load(); }}
        />
      )}
      {moveModal && (
        <MoveStageModal
          opp={moveModal.opp}
          toStage={moveModal.to}
          onClose={() => setMoveModal(null)}
          onDone={(sug) => {
            setMoveModal(null);
            if (sug) showLifecycleSuggestion(sug);
            load();
          }}
        />
      )}
      {historyOpp && (
        <OpportunityHistoryModal opp={historyOpp} onClose={() => setHistoryOpp(null)} />
      )}
    </div>
  );
}


// ============================================================
// Kanban column + card
// ============================================================
function StageColumn({ stage, opps, onOpen, onMove, stages, onHistory }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage.key });
  const total = opps.reduce((s, o) => s + (o.amount || 0), 0);
  return (
    <div
      ref={setNodeRef}
      data-testid={`ana-pipeline-column-${stage.key}`}
      className={"rounded-xl border p-2 flex flex-col min-h-[200px] " + (isOver ? "border-orange-500 bg-orange-50/40" : "border-neutral-200 bg-neutral-50")}
    >
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-xs font-bold uppercase text-neutral-700">{stage.label}</div>
          <div className="text-[10px] text-neutral-500">{opps.length} · {total.toLocaleString("es-ES")} €</div>
        </div>
        <span className="text-[10px] font-semibold rounded-full bg-white border border-neutral-300 text-neutral-600 px-1.5 py-0.5">{stage.default_probability}%</span>
      </div>
      <div className="space-y-1.5 overflow-y-auto max-h-[520px] pr-0.5">
        {opps.map((o) => (
          <DraggableCard key={o.id} opp={o} onOpen={onOpen} onMove={onMove} stages={stages} onHistory={onHistory} />
        ))}
      </div>
    </div>
  );
}

function DraggableCard({ opp, onOpen, onMove, stages, onHistory }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: opp.id });
  const style = { opacity: isDragging ? 0.4 : 1 };
  return (
    <div ref={setNodeRef} style={style} data-testid={`ana-pipeline-card-${opp.id}`}>
      <OpportunityCard opp={opp} onOpen={onOpen} onMove={onMove} stages={stages} onHistory={onHistory} dragProps={{ ...attributes, ...listeners }} />
    </div>
  );
}

function OpportunityCard({ opp, dragging = false, dragProps = {}, onOpen, onMove, stages, onHistory }) {
  return (
    <div className={"rounded-lg border bg-white shadow-sm p-2 " + (dragging ? "border-orange-500" : "border-neutral-200 hover:border-neutral-300")}>
      <div className="flex items-start gap-2">
        <div className="flex-shrink-0 mt-0.5 cursor-grab active:cursor-grabbing text-neutral-400" {...dragProps} title="Arrastra para mover">
          <MoveRight className="h-3.5 w-3.5 rotate-90" />
        </div>
        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={() => onOpen && onOpen(opp)}
            className="text-left w-full"
            data-testid={`ana-opp-open-${opp.id}`}
          >
            <div className="text-xs font-semibold text-neutral-800 truncate">{opp.title}</div>
            <div className="text-[10px] text-neutral-500 truncate">{opp.student?.name || "—"}</div>
          </button>
          <div className="flex items-center gap-2 mt-1 text-[10px] text-neutral-600">
            <span className="inline-flex items-center gap-0.5 font-semibold text-neutral-800"><DollarSign className="h-2.5 w-2.5" />{(opp.amount || 0).toLocaleString("es-ES")} €</span>
            <span className="inline-flex items-center gap-0.5"><Percent className="h-2.5 w-2.5" />{opp.probability}%</span>
            <span className="inline-flex items-center gap-0.5 truncate"><User className="h-2.5 w-2.5" />{(opp.owner_email || "").split("@")[0]}</span>
          </div>
          {stages && !dragging && (
            <div className="flex items-center gap-1 mt-1">
              <select
                value={opp.stage}
                onChange={(e) => onMove && onMove(opp, e.target.value)}
                data-testid={`ana-opp-stage-dropdown-${opp.id}`}
                className="text-[10px] rounded border border-neutral-200 px-1 py-0.5 flex-1 bg-white"
                onClick={(e) => e.stopPropagation()}
              >
                {stages.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
              <button onClick={() => onHistory(opp)} className="text-neutral-400 hover:text-blue-700" title="Historial"><History className="h-3 w-3" /></button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ============================================================
// Table view
// ============================================================
function PipelineTable({ opportunities, stages, onOpen, onMove, onHistory }) {
  return (
    <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden" data-testid="ana-pipeline-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm table-fixed">
          <colgroup>
            <col style={{ width: "180px" }} />
            <col style={{ width: "110px" }} />
            <col />
            <col style={{ width: "110px" }} />
            <col style={{ width: "160px" }} />
            <col style={{ width: "70px" }} />
            <col style={{ width: "160px" }} />
            <col style={{ width: "130px" }} />
            <col style={{ width: "60px" }} />
          </colgroup>
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr className="text-left text-[10px] uppercase text-neutral-500 tracking-wide">
              <th className="px-4 py-3">Alumno</th>
              <th className="px-4 py-3">Producto</th>
              <th className="px-4 py-3">Título</th>
              <th className="px-4 py-3 text-right">Importe</th>
              <th className="px-4 py-3">Stage</th>
              <th className="px-4 py-3 text-right">Prob</th>
              <th className="px-4 py-3">Responsable</th>
              <th className="px-4 py-3 text-right">En stage desde</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {opportunities.map((o) => (
              <tr key={o.id} className="border-b border-neutral-100 hover:bg-neutral-50 h-[56px]" data-testid={`ana-opp-row-${o.id}`}>
                <td className="px-4 py-3 align-middle truncate" title={o.student?.name || ""}>{o.student?.name || "—"}</td>
                <td className="px-4 py-3 text-xs align-middle truncate">{o.product_slug}</td>
                <td className="px-4 py-3 align-middle truncate">
                  <button className="hover:underline text-left w-full truncate" onClick={() => onOpen(o)} title={o.title}>{o.title}</button>
                </td>
                <td className="px-4 py-3 text-right font-semibold align-middle whitespace-nowrap">{(o.amount || 0).toLocaleString("es-ES")} €</td>
                <td className="px-4 py-3 align-middle">
                  <select
                    value={o.stage}
                    onChange={(e) => onMove(o, e.target.value)}
                    data-testid={`ana-opp-table-stage-${o.id}`}
                    className="text-[11px] rounded border border-neutral-200 px-1 py-0.5 bg-white w-full"
                  >
                    {stages.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3 text-right align-middle">{o.probability}%</td>
                <td className="px-4 py-3 text-[11px] align-middle truncate">{o.owner_email}</td>
                <td className="px-4 py-3 text-right text-[11px] text-neutral-500 align-middle whitespace-nowrap">
                  {relativeTime(o.stage_updated_at)}
                </td>
                <td className="px-4 py-3 text-right align-middle">
                  <button onClick={() => onHistory(o)} className="text-neutral-500 hover:text-blue-700" title="Historial"><History className="h-3.5 w-3.5" /></button>
                </td>
              </tr>
            ))}
            {opportunities.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-sm text-neutral-500">Sin oportunidades para este filtro.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function relativeTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const hours = diff / (1000 * 60 * 60);
  if (hours < 24) return `${Math.floor(hours)}h`;
  const days = hours / 24;
  return `${Math.floor(days)}d`;
}


// ============================================================
// Creator / Editor / Move / History modals
// ============================================================
function OpportunityCreator({ catalog, users, onClose, onCreated, defaultStudentId = null }) {
  const [studentId, setStudentId] = useState(defaultStudentId || "");
  const [students, setStudents] = useState([]);
  const [productSlug, setProductSlug] = useState((catalog.products || [])[0]?.slug || "otros");
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("0");
  const [stage, setStage] = useState("nueva");
  const [notes, setNotes] = useState("");
  const [owner, setOwner] = useState("");
  const [source, setSource] = useState("");
  const [expectedClose, setExpectedClose] = useState("");
  const [fInicio, setFInicio] = useState("");
  const [fFin, setFFin] = useState("");
  const [obs, setObs] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (defaultStudentId) return;
    api.get("/students").then(({ data }) => setStudents(data)).catch(() => {});
  }, [defaultStudentId]);


  const save = async () => {
    if (!studentId) { toast.error("Selecciona un alumno"); return; }
    if (!title.trim()) { toast.error("Título obligatorio"); return; }
    setSaving(true);
    try {
      await api.post("/ana/opportunities", {
        student_id: studentId,
        product_slug: productSlug,
        title, amount: parseFloat(amount || "0"),
        stage,
        owner_email: owner || null,
        source: source || null,
        notes, expected_close: expectedClose || null,
        fecha_prevista_inicio: fInicio || null,
        fecha_prevista_fin: fFin || null,
        observaciones_internas: obs,
      });
      toast.success("Oportunidad creada");
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-neutral-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-neutral-200 px-4 py-3">
          <h3 className="font-semibold text-lg">Nueva oportunidad</h3>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-900"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          {!defaultStudentId && (
            <div>
              <label className="text-xs font-semibold text-neutral-700 mb-1 block">Alumno</label>
              <StudentPicker
                value={studentId}
                onChange={setStudentId}
                students={students}
                testId="ana-opp-creator-student-search"
                optionTestIdPrefix="ana-opp-creator-student"
                placeholder="Buscar alumno por nombre o expediente…"
                renderMeta={(s) => s.codigo_expediente || ""}
                triggerClassName="h-9 rounded-lg"
              />
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-semibold text-neutral-700">Producto</label>
              <select value={productSlug} onChange={(e) => setProductSlug(e.target.value)} data-testid="ana-opp-creator-product" className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500">
                {(catalog.products || []).map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-700">Estado inicial</label>
              <select value={stage} onChange={(e) => setStage(e.target.value)} className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500">
                {(catalog.stages || []).map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-700">Título</label>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="ana-opp-creator-title" className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" placeholder="p.ej. Pack 10 clases refuerzo" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-xs font-semibold text-neutral-700">Importe (€)</label>
              <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="ana-opp-creator-amount" className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-700">Responsable</label>
              <select value={owner} onChange={(e) => setOwner(e.target.value)} className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500">
                <option value="">Yo mismo</option>
                {users.map((u) => <option key={u.email} value={u.email}>{u.name || u.email}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-700">Cierre estimado</label>
              <input type="date" value={expectedClose} onChange={(e) => setExpectedClose(e.target.value)} className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-semibold text-neutral-700">Origen (source)</label>
              <input type="text" value={source} onChange={(e) => setSource(e.target.value)} placeholder="llamada, referido, web…" className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
            </div>
            <div />
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-700">Notas</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full rounded-lg border border-neutral-300 bg-white p-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
          </div>
          <div className="rounded-lg border border-dashed border-neutral-300 p-3 bg-neutral-50 space-y-2">
            <div className="text-xs font-semibold text-neutral-600">Planificación (opcional — preparado para futuro Clases OFF)</div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-semibold text-neutral-700">Fecha prevista inicio</label>
                <input type="date" value={fInicio} onChange={(e) => setFInicio(e.target.value)} className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
              </div>
              <div>
                <label className="text-xs font-semibold text-neutral-700">Fecha prevista fin</label>
                <input type="date" value={fFin} onChange={(e) => setFFin(e.target.value)} className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-700">Observaciones internas</label>
              <textarea value={obs} onChange={(e) => setObs(e.target.value)} rows={2} className="w-full rounded-lg border border-neutral-300 bg-white p-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" placeholder="Comentario privado del equipo." />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-neutral-200 px-4 py-3 bg-neutral-50">
          <button onClick={onClose} className="btn-secondary">Cancelar</button>
          <button onClick={save} disabled={saving} className="btn-primary" data-testid="ana-opp-creator-save">
            {saving ? "Guardando…" : "Crear oportunidad"}
          </button>
        </div>
      </div>
    </div>
  );
}

function OpportunityEditor({ opp, catalog, users, onClose, onSaved }) {
  const [state, setState] = useState({ ...opp });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/ana/opportunities/${opp.id}`, {
        product_slug: state.product_slug,
        title: state.title,
        amount: parseFloat(state.amount || 0),
        owner_email: state.owner_email,
        source: state.source,
        notes: state.notes,
        probability: state.probability,
        expected_close: state.expected_close,
        fecha_prevista_inicio: state.fecha_prevista_inicio,
        fecha_prevista_fin: state.fecha_prevista_fin,
        observaciones_internas: state.observaciones_internas,
      });
      toast.success("Oportunidad actualizada");
      onSaved();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("¿Borrar la oportunidad? Se conservará el historial de stages pero desaparecerá del pipeline. Requiere PIN sudo.")) return;
    setDeleting(true);
    try {
      await api.delete(`/ana/opportunities/${opp.id}`);
      toast.success("Oportunidad eliminada");
      onSaved();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setDeleting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-neutral-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-neutral-200 px-4 py-3">
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-lg truncate">{opp.title}</h3>
            <div className="text-xs text-neutral-500">{opp.student?.name} · {opp.stage} · {opp.probability}%</div>
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-900"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-semibold text-neutral-700">Producto</label>
              <select value={state.product_slug} onChange={(e) => setState({ ...state, product_slug: e.target.value })} className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500">
                {(catalog.products || []).map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-700">Responsable</label>
              <select value={state.owner_email || ""} onChange={(e) => setState({ ...state, owner_email: e.target.value })} className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500">
                <option value="">—</option>
                {users.map((u) => <option key={u.email} value={u.email}>{u.name || u.email}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-700">Título</label>
            <input type="text" value={state.title || ""} onChange={(e) => setState({ ...state, title: e.target.value })} className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-xs font-semibold text-neutral-700">Importe (€)</label>
              <input type="number" step="0.01" value={state.amount || 0} onChange={(e) => setState({ ...state, amount: parseFloat(e.target.value) })} className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-700">Probabilidad (%)</label>
              <input type="number" min="0" max="100" value={state.probability ?? 0} onChange={(e) => setState({ ...state, probability: parseInt(e.target.value || "0", 10) })} className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-700">Cierre estimado</label>
              <input type="date" value={(state.expected_close || "").slice(0, 10)} onChange={(e) => setState({ ...state, expected_close: e.target.value })} className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-700">Notas</label>
            <textarea value={state.notes || ""} onChange={(e) => setState({ ...state, notes: e.target.value })} rows={2} className="w-full rounded-lg border border-neutral-300 bg-white p-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
          </div>
          <div className="rounded-lg border border-dashed border-neutral-300 p-3 bg-neutral-50 space-y-2">
            <div className="text-xs font-semibold text-neutral-600">Planificación</div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-semibold text-neutral-700">Inicio</label>
                <input type="date" value={(state.fecha_prevista_inicio || "").slice(0, 10)} onChange={(e) => setState({ ...state, fecha_prevista_inicio: e.target.value })} className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
              </div>
              <div>
                <label className="text-xs font-semibold text-neutral-700">Fin</label>
                <input type="date" value={(state.fecha_prevista_fin || "").slice(0, 10)} onChange={(e) => setState({ ...state, fecha_prevista_fin: e.target.value })} className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-700">Observaciones internas</label>
              <textarea value={state.observaciones_internas || ""} onChange={(e) => setState({ ...state, observaciones_internas: e.target.value })} rows={2} className="w-full rounded-lg border border-neutral-300 bg-white p-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-neutral-200 px-4 py-3 bg-neutral-50">
          <button onClick={remove} disabled={deleting} className="text-xs font-semibold text-red-700 hover:bg-red-50 inline-flex items-center gap-1 px-2 py-1 rounded" data-testid="ana-opp-editor-delete">
            <Trash2 className="h-3.5 w-3.5" /> {deleting ? "Borrando…" : "Borrar (PIN)"}
          </button>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="btn-secondary">Cancelar</button>
            <button onClick={save} disabled={saving} className="btn-primary" data-testid="ana-opp-editor-save">
              {saving ? "Guardando…" : "Guardar cambios"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MoveStageModal({ opp, toStage, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [actualAmount, setActualAmount] = useState(opp.amount || 0);
  const [actualClose, setActualClose] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  const confirm = async () => {
    if (toStage === "no_interesado" && !note.trim()) { toast.error("Nota obligatoria al descartar"); return; }
    setSaving(true);
    try {
      const payload = { to: toStage };
      if (note.trim()) payload.note = note.trim();
      if (toStage === "pagada") {
        payload.actual_amount = parseFloat(actualAmount || 0);
        payload.actual_close = actualClose;
      }
      const { data } = await api.post(`/ana/opportunities/${opp.id}/move-stage`, payload);
      toast.success(`Movida a "${data.opportunity.stage}"`);
      onDone(data.lifecycle_suggestion || null);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-neutral-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-neutral-200 px-4 py-3">
          <h3 className="font-semibold text-lg">Confirmar cambio de estado</h3>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-900"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="text-xs text-neutral-600">
            Vas a mover <span className="font-semibold">{opp.title}</span> a <span className="font-semibold uppercase">{toStage}</span>.
          </div>
          {toStage === "pagada" && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-semibold text-neutral-700">Importe cobrado (€)</label>
                <input type="number" step="0.01" value={actualAmount} onChange={(e) => setActualAmount(e.target.value)} data-testid="ana-move-actual-amount" className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
              </div>
              <div>
                <label className="text-xs font-semibold text-neutral-700">Fecha cobro</label>
                <input type="date" value={actualClose} onChange={(e) => setActualClose(e.target.value)} className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
              </div>
            </div>
          )}
          <div>
            <label className="text-xs font-semibold text-neutral-700">Nota {toStage === "no_interesado" && <span className="text-red-600">(obligatoria)</span>}</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="ana-move-note" className="w-full rounded-lg border border-neutral-300 bg-white p-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" placeholder={toStage === "no_interesado" ? "Motivo del descarte" : "Comentario opcional"} />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-neutral-200 px-4 py-3 bg-neutral-50">
          <button onClick={onClose} className="btn-secondary">Cancelar</button>
          <button onClick={confirm} disabled={saving} className="btn-primary" data-testid="ana-move-confirm">
            {saving ? "Guardando…" : "Confirmar cambio"}
          </button>
        </div>
      </div>
    </div>
  );
}

function OpportunityHistoryModal({ opp, onClose }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/ana/opportunities/${opp.id}/history`).then(({ data }) => setHistory(data.history || [])).finally(() => setLoading(false));
  }, [opp.id]);
  return (
    <div className="fixed inset-0 z-50 bg-neutral-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-neutral-200 px-4 py-3">
          <h3 className="font-semibold text-lg flex items-center gap-2"><History className="h-4 w-4" /> Historial de stages</h3>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-900"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 space-y-2" data-testid="ana-opp-history-list">
          {loading ? (
            <div className="text-sm text-neutral-500">Cargando…</div>
          ) : history.length === 0 ? (
            <div className="text-sm text-neutral-500">Sin historial.</div>
          ) : history.map((h) => (
            <div key={h.id} className="rounded-lg border border-neutral-200 p-2">
              <div className="text-xs font-semibold flex items-center gap-1">
                {h.from_stage || "—"} <ChevronRight className="h-3 w-3" /> {h.to_stage}
              </div>
              <div className="text-[10px] text-neutral-500">
                {new Date(h.at).toLocaleString("es-ES")} · {h.actor_email}
                {h.actual_amount != null && ` · ${h.actual_amount} €`}
              </div>
              {h.note && <div className="text-xs text-neutral-700 mt-1">{h.note}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Export inline OpportunityCreator so the student-side sub-tab can reuse.
export { OpportunityCreator, OpportunityHistoryModal, OpportunityEditor };
